import { UsersIcon } from "@heroicons/react/24/outline";
import { BellIcon } from "@heroicons/react/24/outline";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import { CalendarIcon } from "@heroicons/react/24/outline";
import { JSX } from "react";
import { motion } from "framer-motion";

const styles = {
    root: "relative mx-auto max-w-7xl px-6 py-16 overflow-hidden",
    center: "relative z-10 text-center",
    title: "text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl",
    subtitle: "mt-6 text-lg leading-8 text-gray-600",
    grid: "relative z-10 mt-16 grid grid-cols-1 gap-y-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-8",
    statItem: "text-center group relative hover-lift p-6 rounded-2xl transition-all duration-300",
    glow: "absolute left-1/2 top-0 -translate-x-1/2",
    glowInner: "rounded-full bg-green-200 blur-2xl",
    iconWrap: "flex justify-center items-center mb-4 relative z-10",
    statValue: "text-3xl font-bold text-gray-900",
    statTitle: "mt-2 text-lg font-semibold text-gray-700",
    statDesc: "mt-2 text-base text-gray-600",
    icon: "h-12 w-12 text-green-600"
};

const StatisticsSectionData = [
    {
        title: "Citas gestionadas",
        value: "10K+",
        description: "Citas organizadas eficientemente para profesionales del cuidado animal.",
        icon: <CalendarIcon className={styles.icon} />,
    },
    {
        title: "Clientes satisfechos",
        value: "95%",
        description: "De nuestros usuarios reportan una mejora en la fidelización de clientes.",
        icon: <UsersIcon className={styles.icon} />,
    },
    {
        title: "Mascotas registradas",
        value: "1K+",
        description: "Mascotas con historiales médicos digitales en nuestra plataforma.",
        icon: <ClipboardIcon className={styles.icon} />,
    },
    {
        title: "Notificaciones enviadas",
        value: "10K+",
        description: "Recordatorios automáticos enviados para citas, vacunas y tratamientos.",
        icon: <BellIcon className={styles.icon} />,
    },
]

const StatisticsSection = (): JSX.Element => {
    // Variants para animaciones de los items
    const containerVariants = {
        hidden: {},
        show: {
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, scale: 0.8, y: 40 },
        show: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 80, damping: 12 } }
    };

    const titleVariants = {
        hidden: { opacity: 0, y: 40 },
        show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 60, delay: 0.1 } }
    };
    const subtitleVariants = {
        hidden: { opacity: 0, y: 30 },
        show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 40, delay: 0.3 } }
    };

    return (
        <div className={styles.root}>
            {/* Fondo animado */}
            {/* <div className="absolute inset-0 pointer-events-none">{bubbles}</div> */}
            <div className={styles.center}>
                <motion.h2
                    className={styles.title}
                    variants={titleVariants}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.7 }}
                >
                    Impacto de AppCotas en números
                </motion.h2>
                <motion.p
                    className={styles.subtitle}
                    variants={subtitleVariants}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.7 }}
                >
                    Descubre cómo AppCotas está transformando la gestión de servicios para veterinarias, peluquerías caninas y más.
                </motion.p>
            </div>

            <motion.div
                className={styles.grid}
                variants={containerVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
            >
                {StatisticsSectionData.map((stat, index) => (
                    <motion.div
                        key={index}
                        className={styles.statItem}
                        variants={itemVariants}
                        whileHover={{
                            scale: 1.08,
                            transition: { type: "spring", stiffness: 200, damping: 10 }
                        }}
                    >
                        {/* Glow animado detrás del ícono */}
                        <motion.div
                            className={styles.glow}
                            style={{ zIndex: 0 }}
                            initial={{ opacity: 0.3, scale: 0.9 }}
                            animate={{
                                opacity: [0.3, 0.6, 0.3],
                                scale: [0.9, 1.15, 0.9]
                            }}
                            transition={{
                                duration: 2.5,
                                repeat: Infinity,
                                repeatType: "loop",
                                delay: index * 0.2
                            }}
                        >
                            <div className={styles.glowInner} style={{ width: 60, height: 60 }} />
                        </motion.div>
                        <div className={styles.iconWrap}>
                            {stat.icon}
                        </div>
                        <motion.h3
                            className={styles.statValue}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.2 + index * 0.1, type: "spring", stiffness: 120 }}
                        >
                            {stat.value}
                        </motion.h3>
                        <p className={styles.statTitle}>{stat.title}</p>
                        <p className={styles.statDesc}>{stat.description}</p>
                    </motion.div>
                ))}
            </motion.div>
        </div>
    );
}

export default StatisticsSection;
